import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InstantReflex, ReflexStep } from 'shiftNotes/_types';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

import NoteCardExpandable from './NoteCardExpandable';
import NoteCardGiveAnswerSources from './NoteCardGiveAnswerSources';

interface NoteCardAnswersSectionProps {
  instantReflex: InstantReflex;
  noteId: string;
  initiallyExpanded?: boolean;
}

export default function NoteCardAnswersSection({
  instantReflex,
  noteId,
  initiallyExpanded = false,
}: NoteCardAnswersSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');

  const answers = useMemo(
    () =>
      [...(instantReflex.steps ?? [])]
        .filter((step: ReflexStep) => step.step_type === 'give_answer')
        .sort((a, b) => a.order - b.order),
    [instantReflex.steps]
  );

  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [currentStep, setCurrentStep] = useState(1);

  if (answers.length === 0) return null;

  const totalSteps = answers.length;
  const activeIndex = Math.min(currentStep - 1, totalSteps - 1);
  const activeAnswer = answers[activeIndex];
  const isLastStep = currentStep >= totalSteps;

  const handleToggle = () => setIsExpanded((v) => !v);
  const handlePrev = () => setCurrentStep((s) => Math.max(1, s - 1));
  const handleNext = () => {
    if (isLastStep) return;
    setCurrentStep((s) => Math.min(totalSteps, s + 1));
  };

  const baseLabel = t('noteCardAnswers.title');
  const stepCounter = `${currentStep}/${totalSteps}`;
  const expandedLabel =
    totalSteps > 1 ? `${baseLabel} ${stepCounter}` : baseLabel;
  const collapsedLabel = totalSteps > 1 ? stepCounter : baseLabel;
  const preview = activeAnswer.body || activeAnswer.title || undefined;

  return (
    <NoteCardExpandable
      variant="answers"
      collapsedLabel={collapsedLabel}
      expandedLabel={expandedLabel}
      currentStep={currentStep}
      totalSteps={totalSteps}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      onPrev={handlePrev}
      onNext={handleNext}
      canGoPrev={currentStep > 1}
      canGoNext={!isLastStep}
      showPagination={totalSteps > 1}
      headerPreview={preview}
    >
      {activeAnswer.title && (
        <Styled.ExpandableStepBody>
          {activeAnswer.title}
        </Styled.ExpandableStepBody>
      )}
      {activeAnswer.body && (
        <Styled.ExpandableStepBody>
          {activeAnswer.body}
        </Styled.ExpandableStepBody>
      )}
      <NoteCardGiveAnswerSources
        documentTranslations={instantReflex.rag?.document_translations}
        noteId={noteId}
        reflexId={instantReflex.id}
        stepId={activeAnswer.id}
      />
    </NoteCardExpandable>
  );
}
